/**
 * 🆙 PetLevelBar — shows current pet level, XP bar to next level, HP multiplier.
 * Reads /api/pet/level/state. Mounted on the Pet page.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PetLevelBar() {
  const [state, setState] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/pet/level/state`);
        if (!cancelled) setState(r.data);
      } catch {
        // silent — widget hides itself
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!state) return null;
  const pct = state.is_max ? 100 :
    Math.max(0, Math.min(100, (state.level_xp / Math.max(1, state.xp_to_next)) * 100));
  const recent = (state.level_history || []).slice(-3).reverse();

  return (
    <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-[#0a1a14] via-[#0A0A0A] to-[#08140e] p-4 shadow-[0_0_20px_rgba(16,185,129,0.15)]"
         data-testid="pet-level-bar">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🆙</span>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Pet Level</div>
            <div className="text-2xl font-black text-emerald-300" style={{ fontFamily: 'Anton, sans-serif' }}
                 data-testid="pet-level-value">
              {state.level}
              {state.is_max && <span className="text-amber-300 text-xs ml-2">MAX</span>}
            </div>
          </div>
        </div>
        <div className="text-right text-xs">
          <div className="text-neutral-500">HP boost</div>
          <div className="text-emerald-200 font-bold" data-testid="pet-level-hp-mult">
            ×{state.hp_multiplier.toFixed(2)}
          </div>
        </div>
      </div>

      {!state.is_max ? (
        <>
          <div className="h-2.5 rounded-full bg-neutral-900 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 via-amber-400 to-emerald-400 transition-all"
                 style={{ width: `${pct}%` }} data-testid="pet-level-xp-bar" />
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-neutral-500">
            <span data-testid="pet-level-xp">{state.level_xp.toLocaleString()} XP</span>
            <span data-testid="pet-level-next">/ {state.xp_to_next.toLocaleString()} to Lv {state.level + 1}</span>
          </div>
        </>
      ) : (
        <div className="text-center text-amber-300 text-xs font-bold py-1" data-testid="pet-level-max">
          🏆 Maximum level reached
        </div>
      )}

      {recent.length > 0 && (
        <div className="mt-3 pt-2 border-t border-neutral-800">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Recent dings</div>
          <div className="space-y-0.5 text-[10px] text-neutral-400">
            {recent.map((d, i) => (
              <div key={i} className="flex items-center justify-between" data-testid={`pet-level-ding-${i}`}>
                <span>🎉 Lv {d.to_level}</span>
                <span className="text-emerald-300 font-mono">
                  {d.prev_hp} +{d.hp_delta} = {d.new_hp} HP
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
